import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { hasPermission, type PermissionKey, type UserSummary } from '@oss/shared';

/**
 * Permission checks in the UI, reading the same matrix the API guard enforces.
 *
 * Worth being explicit about what this is and is not: hiding a button is a
 * usability feature, never a security control. The server denies the request
 * regardless. The value is that a user is not shown a destination that will
 * 403 on arrival — and that both sides derive from one definition in
 * `@oss/shared`, so they cannot drift apart.
 */

interface AuthContextValue {
  user: UserSummary | null;
  permissions: Set<PermissionKey>;
}

const AuthContext = createContext<AuthContextValue>({ user: null, permissions: new Set() });

export function AuthProvider({ user, children }: { user: UserSummary | null; children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions: new Set((user?.permissions ?? []) as PermissionKey[]),
    }),
    [user],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

export function usePermissions() {
  const { permissions } = useAuth();
  return useMemo(
    () => ({
      has: (permission: PermissionKey) => hasPermission(permissions, permission),
      hasAny: (...required: PermissionKey[]) =>
        required.some((p) => hasPermission(permissions, p)),
      hasAll: (...required: PermissionKey[]) =>
        required.every((p) => hasPermission(permissions, p)),
    }),
    [permissions],
  );
}

/**
 * Renders children only when the permission is held.
 *
 * `fallback` defaults to nothing rather than to a "you do not have access"
 * message. For most UI — a delete button, an export menu — the right
 * experience is that the control simply is not there. Announcing the existence
 * of things a user cannot do is noise at best, and at worst tells them what to
 * go asking for.
 */
export function PermissionGate({
  permission,
  fallback = null,
  children,
}: {
  permission: PermissionKey;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { has } = usePermissions();
  return <>{has(permission) ? children : fallback}</>;
}
